import React, { useEffect, useRef, memo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActionIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useWindowSize, useDebounce } from '@uidotdev/usehooks';
import { TbCopy } from 'react-icons/tb';
import canvasDatagrid from 'canvas-datagrid';
import { CANVAS_TABLE_STYLES, IMAGE_TYPES, SPECIAL_NULL_STRING } from './constants';
import * as eventUtils from '../../utils/event.utils';
import { formatCellNumber } from '../../utils/formatCellNumber';

const DEFAULT_CELL_FONT_SIZE = 12;

interface SelectedCell {
  rowId: number;
  columnId: number;
  text: unknown;
}

// Schema entry passed through verbatim to canvas-datagrid via
// `grid.schema = schema`. `name` is the key in each row object, `title`
// is the rendered header label.
export interface CanvasTableSchemaColumn {
  name: string;
  title?: string;
  type?: string;
  width?: number;
  hidden?: boolean;
  horizontalAlignment?: 'left' | 'center' | 'right';
}

export interface CanvasTableEditEvent {
  rowIndex: number;
  columnName: string;
  value: unknown;
}

export interface CanvasTableActionEvent {
  rowIndex: number;
  columnName: string;
}

interface CanvasTableProps {
  data: unknown[];
  foundCell?: { rowIndex: number; columnIndex: number } | null;
  zoomLevel: number;
  startLine?: number;
  endLine?: number;
  getCellStyle?: (row: number, col: number) => { background?: string } | undefined;
  onFocusedCell: (cell: SelectedCell) => void;
  setSelectedCells: (cells: SelectedCell[]) => void;
  handleSelection: (cells: SelectedCell[]) => void;
  style?: React.CSSProperties;
  rowPinning: { top: unknown[] };
  columnPinning: { left: unknown[] };
  isStructured?: boolean;
  className?: string;
  scrolledPosition: { top: number; left: number };
  onChangeScrollPosition?: (event: unknown) => void;
  width?: number;
  columnsTypes?: Record<string, string>;
  /** Optional canvas-datagrid schema. When set, the grid uses this instead of
   * inferring columns from the first row of `data`. Required when consumers
   * need stable column ordering or width control across data changes. */
  schema?: CanvasTableSchemaColumn[];
  /** Enable inline editing on the grid. Default `false` — preserves existing
   * read-only usage. */
  editable?: boolean;
  /** Show canvas-datagrid's built-in auto-numbered row header column. Default
   * `true` — preserves existing behaviour. Pass `false` to take full control
   * of the leftmost column via the schema. */
  showRowHeaders?: boolean;
  /** Fired after the user commits an inline edit (canvas-datagrid `endedit`).
   * The grid has already written `value` into `data[rowIndex][columnName]`,
   * so the parent only needs to mirror the change into its own React state. */
  onEditEnd?: (e: CanvasTableEditEvent) => void;
  /** Click handler for cells in columns listed in `actionColumns`
   * (e.g. a synthetic trailing column rendering a trash glyph). Action cells
   * also never enter edit mode (vetoed via `beforebeginedit`). */
  onActionCellClick?: (e: CanvasTableActionEvent) => void;
  /** Column names treated as "action" cells — clicking inside them fires
   * `onActionCellClick` and the cell never enters edit mode even when
   * `editable` is true. */
  actionColumns?: string[];
  /** Theme overrides merged into `grid.style` after the grid is created.
   * Lets consumers reskin the canvas without forking the component. Keys
   * mirror canvas-datagrid's `style.*` properties. */
  gridStyleOverrides?: Record<string, string | number>;
  /** Custom paint callback invoked from canvas-datagrid's `rendercell` event.
   * Used to draw glyphs (e.g. trash icon) or replace the background of
   * specific cells. Calling `event.preventDefault()` skips the default
   * cell paint entirely. */
  onRenderCell?: (event: any) => void;
  /** Custom paint callback invoked at the start of canvas-datagrid's
   * `rendertext` event. The consumer can mutate `event.ctx.font` /
   * `event.ctx.fillStyle` (header rows getting bolder text, row-number
   * column getting smaller text, etc.) — those values are then used by
   * the default text renderer that runs immediately after. Calling
   * `event.preventDefault()` skips the default text paint entirely. */
  onRenderText?: (event: any) => void;
  /** Custom paint callback invoked from canvas-datagrid's `afterrendercell`
   * event — runs AFTER the default cell + text paint, so it lands on top.
   * Useful when the default text path can't be cancelled via preventDefault
   * (e.g. canvas-datagrid's row-header column in some builds). */
  onAfterRenderCell?: (event: any) => void;
  /** Map of `<column name> → width in px`. Any user-initiated resize on a
   * column in this map is snapped back to the supplied width on the next
   * `resizecolumn` tick. Used to lock synthetic action columns. */
  lockedColumnWidths?: Record<string, number>;
  /** Fires after canvas-datagrid natively handles a keyboard-driven cell
   * mutation (Cmd/Ctrl+V paste, Delete/Backspace clear, Cmd/Ctrl+X cut).
   * The grid has already mutated its in-memory data by the time this
   * fires, but it never emits `endedit` for these paths — parents that
   * track a "dirty" flag off `onEditEnd` therefore miss the change.
   * Use this hook to mirror the mutation back into React state. Each
   * cell is `{ rowIndex, columnName, value }` — same shape as
   * `onEditEnd`; `value` for a delete/cut is the empty string.  */
  onAfterExternalEdit?: (cells: CanvasTableEditEvent[]) => void;
  /** Fires once after the canvas-datagrid instance is created and again
   * with `null` on unmount. Lets the parent hold a ref to the grid to
   * call imperative APIs — e.g. `grid.endEdit()` to commit the active
   * cell editor before firing a save. */
  onGridReady?: (grid: any | null) => void;

  hideHoverCopyOnFirstRow?: boolean;
}

// Don't use toast.error in the canvas table because toast library behaves strange with the canvas
// and causes unexpected re-renders of the whole application
const CanvasTable = memo(
  ({
    data,
    foundCell,
    zoomLevel,
    startLine,
    endLine,
    getCellStyle,
    onFocusedCell,
    setSelectedCells,
    handleSelection,
    style,
    rowPinning,
    columnPinning,
    isStructured,
    className,
    key,
    scrolledPosition,
    onChangeScrollPosition,
    width,
    columnsTypes,
    schema,
    editable,
    showRowHeaders,
    onEditEnd,
    onActionCellClick,
    actionColumns,
    gridStyleOverrides,
    onRenderCell,
    onRenderText,
    onAfterRenderCell,
    lockedColumnWidths,
    onAfterExternalEdit,
    onGridReady,
    hideHoverCopyOnFirstRow
  }: CanvasTableProps & { key?: string }) => {
    const [searchParams] = useSearchParams();
    const isTransformationTab = searchParams?.get('tab') === 'transformation';
    const element = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
    const startLineRef = useRef<number | undefined>(startLine);
    startLineRef.current = startLine;
    const endLineRef = useRef<number | undefined>(endLine);
    endLineRef.current = endLine;

    const onFocusedCellRef = useRef(onFocusedCell);
    onFocusedCellRef.current = onFocusedCell;

    const setSelectedCellsRef = useRef(setSelectedCells);
    setSelectedCellsRef.current = setSelectedCells;

    const handleSelectionRef = useRef(handleSelection);
    handleSelectionRef.current = handleSelection;

    const getCellStyleRef = useRef(getCellStyle);
    getCellStyleRef.current = getCellStyle;

    // Hover-anchored copy button — one floating overlay per grid, not per cell.
    const [hoverCopy, setHoverCopy] = useState<{
      top: number;
      left: number;
      value: unknown;
    } | null>(null);

    const onChangeScrollPositionRef = useRef(onChangeScrollPosition);
    onChangeScrollPositionRef.current = onChangeScrollPosition;

    // Refs for the new editing / paint callbacks so the init effect stays
    // stable (it must NOT re-create the grid when these change between
    // renders). Same ref-mirror pattern as the existing focused-cell refs.
    const onEditEndRef = useRef(onEditEnd);
    onEditEndRef.current = onEditEnd;
    const onActionCellClickRef = useRef(onActionCellClick);
    onActionCellClickRef.current = onActionCellClick;
    const actionColumnsRef = useRef(actionColumns);
    actionColumnsRef.current = actionColumns;
    const onRenderCellRef = useRef(onRenderCell);
    onRenderCellRef.current = onRenderCell;
    const onRenderTextRef = useRef(onRenderText);
    onRenderTextRef.current = onRenderText;
    const onAfterRenderCellRef = useRef(onAfterRenderCell);
    onAfterRenderCellRef.current = onAfterRenderCell;
    const lockedColumnWidthsRef = useRef(lockedColumnWidths);
    lockedColumnWidthsRef.current = lockedColumnWidths;
    const onAfterExternalEditRef = useRef(onAfterExternalEdit);
    onAfterExternalEditRef.current = onAfterExternalEdit;
    const onGridReadyRef = useRef(onGridReady);
    onGridReadyRef.current = onGridReady;
    // Set from inside afterpaste / afterdelete / aftercut; consumed by the
    // data-sync effect below to skip its `grid.data = data` reassign the
    // next time the parent re-renders with a new `data` prop. Canvas-
    // datagrid has already mutated its internal data in-place during the
    // event — reassigning would trigger a full recompute + scroll restore
    // attempt, which is visually noisy even when it lands close to the
    // original position.
    const justExternallyEditedRef = useRef(false);

    const multiSelectCellsRef = useRef([]);

    const [grid, setGrid] = useState<any>(null);
    // Ref-mirror of the latest scroll position so it survives across renders
    // without forcing the data-sync effect to depend on the prop reference.
    const scrolledPositionRef = useRef(scrolledPosition);
    scrolledPositionRef.current = scrolledPosition;

    const windowSize = useWindowSize();
    const debouncedWindowSize = useDebounce(windowSize, 100);

    const updateStyles = useCallback(
      grid => {
        if (!grid) return;

        const fontSize = DEFAULT_CELL_FONT_SIZE * zoomLevel;
        const cellHeight = DEFAULT_CELL_FONT_SIZE * 2 * zoomLevel;

        const font = `${fontSize}px 'Gilroy',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    sans-serif`;
        // Capture scroll BEFORE the style writes — canvas-datagrid recomputes
        // its layout on each style assignment and any of these can clamp
        // scrollTop/scrollLeft back to 0. Restoring after the writes keeps
        // the user's scroll position across font/zoom/size churns.
        const prevTop = grid.scrollTop ?? 0;
        const prevLeft = grid.scrollLeft ?? 0;

        grid.style.cellFont = font;
        grid.style.columnHeaderCellFont = font;
        grid.style.rowHeaderCellFont = font;
        grid.style.activeCellFont = font;
        grid.style.cellHeight = cellHeight;
        grid.style.columnHeaderCellHeight = cellHeight;
        const rect = element.current.getBoundingClientRect();
        const nextWidth = rect.width + 'px';
        const nextHeight = rect.height + 'px';
        if (grid.style.width !== nextWidth) grid.style.width = nextWidth;
        if (grid.style.height !== nextHeight) grid.style.height = nextHeight;

        try {
          grid.scrollTop = prevTop;
          grid.scrollLeft = prevLeft;
        } catch (_) {
          /* swallow */
        }
      },
      [grid, zoomLevel, element]
    );

    useEffect(() => {
      if (!element.current) return;

      const grid = canvasDatagrid({
        allowFreezingColumns: true,
        allowFreezingRows: true, // freezing line appears on top-left corner of the table
        showFilter: true, // filter option in the context menu (appears when you right-click on the table)
        showClearSettingsOption: true, // hides clear settings in the context menu options
        // Built-in auto-numbered row header column. Consumers can disable
        // (e.g. lookup tables that want full control of the leftmost column).
        showRowHeaders: showRowHeaders !== false,
        parentNode: element.current,
        editable: !!editable,
        allowSorting: false,
        maxPixelRatio: 3, // by default 1.5 (1920x1080), 3 for (4K screens)
        showPerformance: false, // if set to "true" shows graphical chart of the performance
        selectionFollowsActiveCell: true, // otherwise active and selected cells are different
        keepFocusOnMouseOut: true // grid will continue to handle keydown events when mouse is outside the grid
        // selectionFollowsActiveCell: true,
        // allowRowResizeFromCell: true, // resizing each cell
        // borderDragBehavior: 'resize',
        // singleSelectionMode: true, // no effect so far
        // selectionHandleBehavior: 'ismoil', // no effect so far
      });

      grid.style.width = element.current.getBoundingClientRect().width + 'px';
      grid.style.height = element.current.getBoundingClientRect().height + 'px';

      // Hide canvas-datagrid's built-in auto-numbered row-header column.
      // Different builds expose different levers; spray every plausible name
      // so whichever one this build honours actually takes effect.
      if (showRowHeaders === false) {
        const attrNames = [
          'showRowHeaders',
          'showRowNumbers',
          'showRowSelectors',
          'rowSelectorVisible',
          'showHeaders'
        ];
        for (const name of attrNames) {
          try {
            grid.attributes[name] = false;
          } catch (_) {
            /* swallow */
          }
        }
        const styleNames = [
          'rowHeaderCellWidth',
          'rowHeaderWidth',
          'gutterCellWidth',
          'gutterWidth',
          'rowSelectorCellWidth'
        ];
        for (const name of styleNames) {
          try {
            grid.style[name] = 0;
          } catch (_) {
            /* swallow */
          }
        }
      }
      // grid.style.columnHeaderCellBackgroundColor = '#eef0f4';
      // grid.style.rowHeaderCellBackgroundColor = '#eef0f4';
      grid.style.columnHeaderCellHorizontalAlignment = 'center';
      grid.style.rowHeaderCellHorizontalAlignment = 'center';
      // grid.style.activeCellBorderColor = 'red';
      // grid.style.activeCellBorderWidth = 3;
      grid.style.activeCellOverlayBorderWidth = 1.5;
      updateStyles(grid);
      // grid.style.editCellBackgroundColor = 'red';
      // grid.style.groupIndicatorBackgroundColor = 'red';
      // grid.style.cellSelectedBackgroundColor = 'red';

      const handleGridTableCopy = () => {
        const selectedCells = grid.selectedCells || [];
        if (!selectedCells.length) return;

        try {
          let formattedData = '';
          const validCells = selectedCells.filter(cell => cell !== null);
          const rows = validCells
            .map(cell => {
              const values = Object.values(cell);
              return values.join('\t');
            })
            .filter(row => row.trim() !== '');
          formattedData = rows.join('\n');
          navigator.clipboard.writeText(formattedData).catch(error => {
            console.log(`handleGridTableCopy ${error}`);
          });
        } catch (error) {
          console.log(`handleGridTableCopy ${error}`);
        }
      };

      const unhideAllColumns = () => {
        grid.schema.forEach(col => {
          col.hidden = false;
        });

        grid.draw();
      };

      const originalFilter = grid.filters.string;

      grid.filters.string = function (cellText, searchText) {
        try {
          // In transformation - first row=0 is always a header row, so we never filter out the header row
          // In extraction - we don't know which row is a header row, in this case the "mouse focused row" will not be filtered out
          let currentCellValues = Object.values(grid.currentCell.data);
          if (isTransformationTab) {
            currentCellValues = Object.values(grid.data[0]);
          }
          if (currentCellValues.includes(cellText)) return true;
          return originalFilter(cellText, searchText);
        } catch (error) {
          console.log(`grid.filters ${error}`);
          return originalFilter(cellText, searchText);
        }
      };

      // after the cell is rendered, draw on top of it
      grid.addEventListener('afterrendercell', function (e) {
        try {
          if (onAfterRenderCellRef.current) {
            // For row-header cells we erase the cell area first so canvas-
            // datagrid's default (and possibly bold dark) digit text is
            // wiped before the consumer paints. This is belt-and-braces
            // beside the colour-mirror effect above — the style-key trick
            // doesn't land on every canvas-datagrid build, but a fillRect
            // always wins.
            const cell = e.cell;
            const isRowHeader =
              cell?.style === 'rowHeaderCell' ||
              cell?.isRowHeader === true ||
              cell?.rowHeaderCell === true ||
              cell?.header?.style === 'rowHeaderCell' ||
              ((cell?.columnIndex == null || cell?.columnIndex === -1) &&
                cell?.rowIndex != null &&
                cell?.rowIndex >= 0);
            if (isRowHeader && e.ctx) {
              const bg =
                gridStyleOverrides?.rowHeaderCellBackgroundColor ??
                grid.style.rowHeaderCellBackgroundColor ??
                '#ffffff';
              e.ctx.save();
              try {
                e.ctx.fillStyle = bg;
                e.ctx.fillRect(cell.x, cell.y, cell.width, cell.height);
              } finally {
                e.ctx.restore();
              }
            }
            // Consumer-supplied post-paint hook — runs AFTER canvas-datagrid's
            // default cell + text paint, so any pixels we put down here land
            // on top.
            onAfterRenderCellRef.current(e);
          }
        } catch (error) {
          console.log(`afterrendercell ${error}`);
        }

        if (e.cell.rowIndex === 0 && e.cell.columnIndex !== -1 && columnsTypes) {
          const i = IMAGE_TYPES?.[columnsTypes[e.cell.columnIndex]]?.();

          if (i && i?.width !== 0) {
            i.targetHeight = e.cell.height;
            i.targetWidth = e.cell.height * (i?.width / i?.height);
            e.ctx.drawImage(i, e.cell.x + 4, e.cell.y + 4, 16, 16);
          }
        }
      });

      grid.addEventListener('rendercell', function (e) {
        try {
          // Consumer-supplied paint hook — runs first so it can draw glyphs
          // (e.g. trash icon in an action column), repaint backgrounds, or
          // fully take over via `event.preventDefault()`.
          if (onRenderCellRef.current) {
            onRenderCellRef.current(e);
            if (e.defaultPrevented) return;
          }

          const isHeaderRow = e.cell.rowIndex === startLineRef.current! - 1;
          const isFooterRow = e.cell.rowIndex === endLineRef.current! - 1;

          if (e.cell.rowIndex === 0) {
            const ctx = e.ctx;
            const { x, y, height } = e.cell;

            ctx.fillText(e.cell.value, x + 26, y + height / 2);
          }

          if (isHeaderRow && isStructured) {
            e.ctx.fillStyle = CANVAS_TABLE_STYLES.COLORS.LIGHT_BLUE;
          } else if (isFooterRow && isStructured) {
            e.ctx.fillStyle = CANVAS_TABLE_STYLES.COLORS.LIGHT_BLUE;
            e.ctx.strokeStyle = CANVAS_TABLE_STYLES.COLORS.BLACK;
            e.ctx.lineWidth = 1.25;
          } else if (getCellStyleRef.current) {
            const cellBackground = getCellStyleRef.current(
              e.cell.rowIndex + 2,
              e.cell.columnIndex + 1
            )?.background;
            if (cellBackground && cellBackground !== '#fff') {
              e.ctx.fillStyle = cellBackground;
            }
          }
        } catch (error) {
          console.log(`rendercell ${error}`);
        }
      });

      function isQuotedNumber(value) {
        if (typeof value !== 'string') return false;
        const trimmed = value.trim();
        if (trimmed.length < 2) return false;
        const isWrapped =
          (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
          (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (!isWrapped) return false;

        const unquotedValue = trimmed.slice(1, -1);
        return !isNaN(Number(unquotedValue)) && unquotedValue !== '';
      }

      grid.addEventListener('rendertext', function (e) {
        try {
          // Consumer-supplied text styling hook — runs first so it can mutate
          // ctx.font / ctx.fillStyle ahead of either the manual fillText
          // branches below or the default text renderer that fires after.
          if (onRenderTextRef.current) {
            onRenderTextRef.current(e);
            if (e.defaultPrevented) return;
          }

          if (isQuotedNumber(e.cell.value)) {
            const fixedText = `"${e.cell.value}"`;
            e.ctx.fillText(fixedText, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          }

          if (
            e.cell.type === 'string' &&
            e.cell.value?.includes?.(' - ') &&
            e.cell.rowIndex !== 0
          ) {
            const fixedText = e.cell.value.replace(/ - /g, ' - ');
            e.ctx.fillText(fixedText, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          } else if (e.cell.rowIndex === 0 && e.cell.columnIndex !== -1 && columnsTypes) {
            e.ctx.fillText(e.cell.value, e.cell.x + 26, e.cell.y + e.cell.height / 2 + 4);
            e.preventDefault();
            return;
          }

          // Thousand-separator render for plain numeric data-row cells.
          // Underlying value untouched — copy / filter / search still see raw.
          if (e.cell.rowIndex !== 0) {
            const formatted = formatCellNumber(e.cell.value);
            if (formatted !== null) {
              e.ctx.fillText(formatted, e.cell.x + 5, e.cell.y + e.cell.height / 2 + 4);
              e.preventDefault();
              return;
            }
          }
        } catch (err) {
          console.log(`rendertext ${err}`);
        }

        if (e.cell.value === SPECIAL_NULL_STRING) {
          e.ctx.fillStyle = '#aaa';
        }
      });

      grid.addEventListener('click', function (event) {
        try {
          // Action columns (delete-row trash glyph etc.) consume the click —
          // fire the action callback and skip the normal selection plumbing
          // so the cell never moves into edit mode either.
          const colName = event.cell?.header?.name;
          const actionCols = actionColumnsRef.current;
          if (
            colName &&
            Array.isArray(actionCols) &&
            actionCols.includes(colName) &&
            onActionCellClickRef.current
          ) {
            event.preventDefault?.();
            onActionCellClickRef.current({
              rowIndex: event.cell.rowIndex,
              columnName: colName
            });
            return;
          }

          const selectionResult = {
            rowId: event.cell.rowIndex + 1,
            columnId: event.cell.columnIndex + 1,
            text: event.cell.value
          };

          onFocusedCellRef.current(selectionResult);
          setSelectedCellsRef.current([selectionResult]);
          handleSelectionRef.current([selectionResult]);
        } catch (error) {
          console.log(`click ${error}`);
        }
      });

      // Action cells must never enter edit mode even when `editable: true`.
      grid.addEventListener('beforebeginedit', function (event) {
        try {
          const colName = event.cell?.header?.name;
          const actionCols = actionColumnsRef.current;
          if (colName && Array.isArray(actionCols) && actionCols.includes(colName)) {
            event.preventDefault();
          }
        } catch (error) {
          console.log(`beforebeginedit ${error}`);
        }
      });

      // Mirror committed edits back to the consumer. canvas-datagrid has
      // already written `value` into data[rowIndex][columnName] by the time
      // this fires — the consumer only needs to bump its dirty flag /
      // re-render dependent UI.
      grid.addEventListener('endedit', function (event) {
        try {
          if (event.abort) return;
          const colName = event.cell?.header?.name;
          if (!colName || !onEditEndRef.current) return;
          onEditEndRef.current({
            rowIndex: event.cell.rowIndex,
            columnName: colName,
            value: event.value
          });
        } catch (error) {
          console.log(`endedit ${error}`);
        }
      });

      // canvas-datagrid natively handles paste / delete / cut on keydown —
      // it mutates its own in-memory data, dispatches
      // `afterpaste` / `afterdelete` / `aftercut`, but never fires
      // `endedit` for any of them. Consumers that track dirty off
      // `onEditEnd` would miss every one of these edits (their React
      // state doesn't know), so we surface them through a shared
      // `onAfterExternalEdit` callback.
      //
      // All three events share the same payload:
      //   `{ cells: [[viewRowIndex, viewColumnIndex,
      //             boundRowIndex, boundColumnIndex], …] }`
      // POSITIONS ONLY, no value in the tuple. The mutated values live
      // on `grid.data` at the bound coordinates by the time we receive
      // the event, so we read them back from there — that gives '' /
      // undefined for delete / cut and the pasted string for paste.
      const decodeAndForward = (event: any, sourceLabel: string) => {
        try {
          if (!onAfterExternalEditRef.current) return;
          const raw: any[] = Array.isArray(event?.cells) ? event.cells : [];
          const decoded: CanvasTableEditEvent[] = [];
          for (const tuple of raw) {
            if (!Array.isArray(tuple) || tuple.length < 4) continue;
            const [, , boundRowIndex, boundColumnIndex] = tuple;
            if (boundRowIndex == null || boundRowIndex < 0) continue;
            const schemaEntry = grid.schema?.[boundColumnIndex];
            const colName = schemaEntry?.name;
            if (!colName) continue;
            const value = grid.data?.[boundRowIndex]?.[colName];
            decoded.push({ rowIndex: boundRowIndex, columnName: colName, value });
          }
          if (decoded.length > 0) {
            // Tell the data-sync effect to skip the next `grid.data =` —
            // canvas-datagrid already applied the mutation to its in-memory
            // data, so reassigning would just trigger a redundant recompute
            // that visibly jogs the scroll position.
            justExternallyEditedRef.current = true;
            onAfterExternalEditRef.current(decoded);
          }
        } catch (error) {
          console.log(`${sourceLabel} ${error}`);
        }
      };
      grid.addEventListener('afterpaste', (e: any) => decodeAndForward(e, 'afterpaste'));
      grid.addEventListener('afterdelete', (e: any) => decodeAndForward(e, 'afterdelete'));
      grid.addEventListener('aftercut', (e: any) => decodeAndForward(e, 'aftercut'));

      // Snap locked-width columns back if a user tries to drag-resize them.
      grid.addEventListener('resizecolumn', function (event) {
        try {
          const locks = lockedColumnWidthsRef.current;
          if (!locks) return;
          const colName = event.column?.name || event.columnName || event.cell?.header?.name;
          if (colName && locks[colName] != null) {
            const target = locks[colName];
            if (event.column) event.column.width = target;
            const schemaEntry = grid.schema?.find?.((s: any) => s?.name === colName);
            if (schemaEntry) schemaEntry.width = target;
            grid.draw?.();
          }
        } catch (error) {
          console.log(`resizecolumn ${error}`);
        }
      });

      grid.addEventListener('selectionchanged', event => {
        if (Array.isArray(event.selectedCells) && event.selectedCells.length > 0) {
          const seenRows = new Set(); // each row can only have 1 selection
          const formattedCells = event.selectedCells
            .filter(cell => {
              const rowId = cell.viewRowIndex;
              if (seenRows.has(rowId)) return false;
              seenRows.add(rowId);
              return true;
            })
            .map(cell => ({
              rowId: cell.viewRowIndex + 1,
              columnId: cell.viewColumnIndex + 1,
              text: cell.value
            }));

          if (!isStructured) multiSelectCellsRef.current = formattedCells;
        }
      });

      // Hover-anchored copy button — track the cell under the cursor and
      // surface a small overlay button at its right edge. Single DOM node
      // anchored via state — no per-cell DOM, no extra canvas draws.
      grid.addEventListener('mousemove', event => {
        try {
          const cell = event.cell;
          const actionCols = actionColumnsRef.current;
          const isActionCell =
            cell?.header?.name && Array.isArray(actionCols)
              ? actionCols.includes(cell.header.name)
              : false;
          if (
            !cell ||
            cell.rowIndex == null ||
            cell.rowIndex < 0 ||
            cell.columnIndex == null ||
            cell.columnIndex < 0 ||
            (hideHoverCopyOnFirstRow && cell.rowIndex === 0) ||
            isActionCell
          ) {
            setHoverCopy(prev => (prev === null ? prev : null));
            return;
          }
          const top = cell.y + 2;
          const left = cell.x + cell.width - 22;
          setHoverCopy(prev =>
            prev && prev.top === top && prev.left === left && prev.value === cell.value
              ? prev
              : { top, left, value: cell.value }
          );
        } catch (err) {
          console.log(`hover-copy mousemove ${err}`);
        }
      });

      // 'mouseup' event is also used for multi selection, it works in a similar way to 'keyup' event
      grid.addEventListener('mouseup', event => {
        const nativeEvent = event.NativeEvent;
        // don't select multiple cells if ctrl/cmd is pressed
        if (eventUtils.isCommandKey(nativeEvent)) return;

        const isMultiSelect =
          Array.isArray(multiSelectCellsRef.current) && multiSelectCellsRef.current.length > 1;

        if (isMultiSelect) {
          handleSelectionRef.current(multiSelectCellsRef.current);
          multiSelectCellsRef.current = [];
        }
      });

      // 'keydown' is used to copy selected cell values to clipboard
      grid.addEventListener('keydown', event => {
        const nativeEvent = event.NativeEvent;
        if (eventUtils.isCommandKey(nativeEvent) && nativeEvent.key === 'c') {
          handleGridTableCopy();
        }
      });

      // used for vertical multi selection using 'shift' key
      grid.addEventListener('keyup', event => {
        const nativeEvent = event.NativeEvent;
        if (nativeEvent.keyCode === 16) {
          const gridSelectedCells = Array.isArray(grid.selectedCells) ? grid.selectedCells : [];
          const formattedCells = gridSelectedCells
            .map((item, rowIndex) => {
              if (item && typeof item === 'object') {
                const columnKey = Object.keys(item)[0];
                return {
                  rowId: rowIndex + 1,
                  columnId: parseInt(columnKey) + 1,
                  text: item[columnKey]
                };
              }
              return null;
            })
            .filter(Boolean);

          if (formattedCells?.length > 1) {
            handleSelectionRef.current(formattedCells);
          }
        }
      });

      grid.addEventListener('contextmenu', e => {
        if (!e.items) return;

        if (e.cell && e.cell.header) {
          e.items.push({
            title: 'Unhide All Columns',
            click: () => {
              unhideAllColumns();
            }
          });
        }
      });

      const handleGridScroll = event => {
        try {
          onChangeScrollPositionRef.current?.(event);
          // Hide the hover-copy overlay during scroll — the cell rect moves,
          // and the next mousemove will reposition it cleanly.
          setHoverCopy(prev => (prev === null ? prev : null));
        } catch (error) {
          console.log(`onChangeScrollPosition ${error}`);
        }
      };

      const handleDocumentKeydown = event => {
        try {
          // used to close popup menu on `esc` key
          if (event.key === 'Escape') {
            grid?.disposeContextMenu?.();
            // disposeContextMenu removes the parent popup, but search suggestions still remain open
            // so we have to come up with custom element removal
            document.querySelectorAll('.canvas-datagrid-context-menu').forEach(el => {
              el.remove();
            });
          }
        } catch (err) {
          console.log(`Canvas table keydown event err`, err);
        }
      };

      document.addEventListener('keydown', handleDocumentKeydown);
      grid.addEventListener('scroll', handleGridScroll);

      // Initial data assignment + scroll restore. Subsequent `data` changes
      // are handled by the dedicated data-sync effect below, so the grid
      // instance is no longer torn down + recreated when the parent passes
      // a new array reference.
      grid.data = data;

      try {
        grid.scrollTop = scrolledPosition.top;
        grid.scrollLeft = scrolledPosition.left;
      } catch (err) {
        console.log(err);

        grid.scrollTop = 0;
        grid.scrollLeft = 0;
      }

      setGrid(grid);
      onGridReadyRef.current?.(grid);

      return () => {
        document.removeEventListener('keydown', handleDocumentKeydown);
        grid.removeEventListener('scroll', handleGridScroll);
        onGridReadyRef.current?.(null);
        grid.dispose();
      };
      // `data` and `scrolledPosition` are intentionally NOT in the deps —
      // we don't want to dispose/recreate the grid (which resets scroll)
      // on every new `data` array reference. The data-sync effect below
      // handles in-place data updates instead.
    }, [isStructured, columnsTypes]);

    // Sync `data` into the existing grid instance without recreating it.
    // canvas-datagrid snaps scroll to (0, 0) when `grid.data =` is assigned,
    // so we (a) skip the assignment entirely when the new array has the same
    // content as the current one (most common case on prompt-save refetches),
    // and (b) restore scroll synchronously + rAF + setTimeout so whichever
    // timing canvas-datagrid finishes its internal recompute by, our restore
    // wins.
    useEffect(() => {
      if (!grid) return;
      if (grid.data === data) return;

      // Post-external-edit short-circuit: canvas-datagrid already applied
      // the paste / delete / cut to its in-memory data during its own
      // event handler. The parent then re-renders with a fresh `data`
      // prop (so React state and the dirty flag stay in sync), but
      // reassigning `grid.data =` here would trigger a redundant
      // recompute + scroll restore that visibly jogs the position. Point
      // `grid.data` at the new array reference WITHOUT going through the
      // setter — canvas-datagrid sees no change and keeps its current
      // scroll / selection state.
      if (justExternallyEditedRef.current) {
        justExternallyEditedRef.current = false;
        try {
          Object.defineProperty(grid, '_data', { value: data, configurable: true, writable: true });
        } catch (_) {
          /* older canvas-datagrid builds may not expose `_data`; fall through */
        }
        return;
      }

      // Content-equality short-circuit: skip the reassign (and the scroll
      // reset that comes with it) whenever the incoming `data` describes
      // the same spreadsheet content as what's already in the grid. Two
      // tiers:
      //   1. Fast path — length match + first/last row REFERENCES match.
      //      Wins when React Query hands us a fresh array reference after
      //      a refetch but the row objects themselves are unchanged.
      //   2. Deep path — length matches but references differ. Compare
      //      every row's own-keys' values shallow-equal. Wins after a save
      //      where the server returns the same values it just accepted
      //      but as brand-new objects, so the fast path can't detect it.
      const oldData = grid.data;
      if (
        Array.isArray(oldData) &&
        Array.isArray(data) &&
        oldData.length === data.length &&
        oldData.length > 0
      ) {
        if (oldData[0] === data[0] && oldData[oldData.length - 1] === data[data.length - 1]) {
          return;
        }
        // Deep content check — bounded to `data.length * columns`, runs at
        // most once per state update. Bails out on the first mismatch, so
        // genuine data changes still fall through to the reassign path.
        let contentMatches = true;
        for (let i = 0; i < data.length && contentMatches; i++) {
          const nextRow = data[i] as Record<string, unknown> | undefined;
          const currRow = oldData[i] as Record<string, unknown> | undefined;
          if (nextRow === currRow) continue;
          if (!nextRow || !currRow) {
            contentMatches = false;
            break;
          }
          const nextKeys = Object.keys(nextRow);
          const currKeys = Object.keys(currRow);
          if (nextKeys.length !== currKeys.length) {
            contentMatches = false;
            break;
          }
          for (const key of nextKeys) {
            if (nextRow[key] !== currRow[key]) {
              contentMatches = false;
              break;
            }
          }
        }
        if (contentMatches) return;
      }

      const prevTop = grid.scrollTop ?? scrolledPositionRef.current?.top ?? 0;
      const prevLeft = grid.scrollLeft ?? scrolledPositionRef.current?.left ?? 0;
      // `visibleRows` is the row-index array canvas-datagrid keeps in sync
      // with the current viewport. Capturing the first visible row index
      // lets us re-anchor by semantic position (row N) instead of by raw
      // pixel scroll, which survives the library's internal scroll-reset
      // on `grid.data =`.
      const prevFirstVisibleRow = Array.isArray(grid.visibleRows) ? grid.visibleRows[0] : null;

      if (typeof grid.selectNone === 'function') {
        grid.selectNone(true);
      }

      grid.data = data;

      const restore = () => {
        try {
          // Prefer `gotoCell` (semantic: "show this row index at the top")
          // — it survives recompute cycles where the pixel-based
          // scrollTop/scrollLeft gets clamped to 0.
          if (
            prevFirstVisibleRow != null &&
            typeof grid.gotoCell === 'function' &&
            data &&
            prevFirstVisibleRow < data.length
          ) {
            grid.gotoCell(0, prevFirstVisibleRow, 0, 0);
          } else {
            grid.scrollTop = prevTop;
          }
          grid.scrollLeft = prevLeft;
        } catch (_) {
          // canvas-datagrid can throw when restoring to an out-of-range
          // position (new data shorter than previous scroll). Fall back to
          // 0/0 — better than leaving the grid in an undefined state.
          try {
            grid.scrollTop = 0;
            grid.scrollLeft = 0;
          } catch (__) {
            /* swallow */
          }
        }
      };

      // Restore at three points: synchronously (in case canvas-datagrid
      // hasn't recomputed yet and our write sticks), on the next animation
      // frame (after the library's typical internal recompute), and on a
      // macrotask (last resort for libraries that defer past rAF).
      restore();
      const frame = requestAnimationFrame(restore);
      const timer = window.setTimeout(restore, 0);

      return () => {
        cancelAnimationFrame(frame);
        window.clearTimeout(timer);
      };
    }, [data, grid]);

    useEffect(() => {
      function gotoCell() {
        try {
          grid.selectArea({
            top: foundCell!.rowIndex - 1,
            left: foundCell!.columnIndex - 1
          });
          grid.gotoCell(foundCell!.columnIndex - 1, foundCell!.rowIndex - 1, 0.2, 0.2);
        } catch (err) {
          console.log(`gotoCell ${err}`);
        }
      }

      if (foundCell && grid) {
        gotoCell();
      }
    }, [foundCell, grid]);

    useEffect(() => {
      updateStyles(grid);
      grid?.draw();
    }, [grid, zoomLevel, startLine, endLine]);

    const handlePinning = useCallback(
      (pinningLength, visibleSize, frozenKey) => {
        if (pinningLength > 0) {
          if (visibleSize >= pinningLength) {
            grid[frozenKey] = pinningLength;
          } else {
            if (!isTransformationTab) {
              // @ts-ignore
              notifications.show({
                color: 'orange',
                title:
                  'Cannot freeze panes — too few visible rows/columns. Open the table in full screen and retry.',
                position: 'bottom-center',
                autoClose: 3500
              });
            }
            console.warn('Unable to freeze panes - not enough visible rows/columns to support it', {
              pinningLength,
              visibleSize,
              frozenKey
            });
          }
        } else {
          grid[frozenKey] = 0;
        }
      },
      [grid]
    );

    useEffect(() => {
      if (!grid || !element.current || typeof ResizeObserver === 'undefined') return;

      // Reassigning `grid.style.width`/`height` triggers canvas-datagrid's
      // internal layout recompute, which can clamp the scroll position back
      // to (0, 0). To preserve scroll on container size changes (e.g. a
      // popover closing + sidebar reflow on prompt save), we (a) skip the
      // assignment when dimensions actually didn't change, and (b) capture
      // + restore scroll around any size change that does happen.
      const applySize = () => {
        if (!element.current) return;
        const rect = element.current.getBoundingClientRect();
        const nextWidth = rect.width + 'px';
        const nextHeight = rect.height + 'px';
        if (grid.style.width === nextWidth && grid.style.height === nextHeight) return;

        const prevTop = grid.scrollTop ?? 0;
        const prevLeft = grid.scrollLeft ?? 0;
        grid.style.width = nextWidth;
        grid.style.height = nextHeight;
        grid.draw?.();
        try {
          grid.scrollTop = prevTop;
          grid.scrollLeft = prevLeft;
        } catch (_) {
          /* swallow */
        }
      };

      const observer = new ResizeObserver(applySize);
      observer.observe(element.current);

      return () => {
        observer.disconnect();
      };
    }, [grid]);

    useEffect(() => {
      if (!grid) return;

      const visibleRowsSize = grid.visibleRows.length - 5;
      const visibleColumnsSize = grid.visibleRows.length - 5;

      handlePinning(rowPinning.top.length, visibleRowsSize, 'frozenRow');
      handlePinning(columnPinning.left.length, visibleColumnsSize, 'frozenColumn');
    }, [rowPinning, columnPinning, grid]);

    useEffect(() => {
      if (!grid) return;

      // Same scroll-preservation pattern as the ResizeObserver above —
      // window resizes / `width` prop churns must not reset the user's
      // scroll position.
      const rect = element.current.getBoundingClientRect();
      const nextWidth = rect.width + 'px';
      const nextHeight = rect.height + 'px';
      if (grid.style.width === nextWidth && grid.style.height === nextHeight) return;

      const prevTop = grid.scrollTop ?? 0;
      const prevLeft = grid.scrollLeft ?? 0;
      grid.style.width = nextWidth;
      grid.style.height = nextHeight;
      try {
        grid.scrollTop = prevTop;
        grid.scrollLeft = prevLeft;
      } catch (_) {
        /* swallow */
      }
    }, [debouncedWindowSize, grid, style, width]);

    useEffect(() => {
      if (element.current) {
        const preventDefault = e => {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        };
        element.current.addEventListener('wheel', preventDefault, {
          passive: false
        });
        element.current.addEventListener('touchmove', preventDefault, {
          passive: false
        });
        return () => {
          element.current?.removeEventListener('wheel', preventDefault);
          element.current?.removeEventListener('touchmove', preventDefault);
        };
      }
    }, [element]);

    useEffect(() => {
      if (!grid) return;

      grid.draw();
    }, [getCellStyle, grid]);

    // Apply / update the explicit schema. canvas-datagrid otherwise infers
    // columns from the first row of `data`, which silently re-orders columns
    // when a row arrives with keys in a different order.
    useEffect(() => {
      if (!grid || !schema) return;
      try {
        grid.schema = schema;
        grid.draw?.();
      } catch (error) {
        console.log(`schema sync ${error}`);
      }
    }, [grid, schema]);

    // Toggle editable on the live grid (e.g. switching between read-only and
    // editable dictionaries without remounting).
    useEffect(() => {
      if (!grid) return;
      try {
        grid.attributes.editable = !!editable;
      } catch (error) {
        console.log(`editable sync ${error}`);
      }
    }, [grid, editable]);

    // Consumer-supplied style overrides (colours, borders, padding, etc.).
    // Applied after the init effect's `updateStyles` so the consumer wins.
    useEffect(() => {
      if (!grid || !gridStyleOverrides) return;
      try {
        for (const [key, value] of Object.entries(gridStyleOverrides)) {
          grid.style[key] = value;
        }
        grid.draw?.();
      } catch (error) {
        console.log(`gridStyleOverrides sync ${error}`);
      }
    }, [grid, gridStyleOverrides]);

    // When a consumer is taking over the row-header column via the
    // `afterrendercell` paint hook, hide canvas-datagrid's own digit text
    // by painting it fully transparent across every state variant. The
    // active row uses a different style key than non-active rows in this
    // build — spray every plausible name so the active row's digit is
    // covered too.
    useEffect(() => {
      if (!grid || !onAfterRenderCell) return;
      try {
        const transparent = 'rgba(0, 0, 0, 0)';
        const colorKeys = [
          'rowHeaderCellColor',
          'rowHeaderCellHoverColor',
          'rowHeaderCellSelectedColor',
          'rowHeaderCellActiveColor',
          'activeRowHeaderCellColor',
          'selectedRowHeaderCellColor',
          'rowSelectorCellColor',
          'rowSelectorCellSelectedColor',
          'rowSelectorCellActiveColor',
          'activeCellRowHeaderColor',
          'selectedHeaderCellColor'
        ];
        for (const key of colorKeys) {
          try {
            grid.style[key] = transparent;
          } catch (_) {
            /* swallow */
          }
        }
        grid.draw?.();
      } catch (error) {
        console.log(`row-header text hide ${error}`);
      }
    }, [grid, onAfterRenderCell, gridStyleOverrides]);

    // Lock specific column widths. Applied once after init (and again when
    // the lock map changes) so initial widths match — the `resizecolumn`
    // listener installed in the init effect handles runtime drag attempts.
    useEffect(() => {
      if (!grid || !lockedColumnWidths) return;
      try {
        const list = Array.isArray(grid.schema) ? grid.schema : [];
        for (const [name, width] of Object.entries(lockedColumnWidths)) {
          const entry = list.find((s: any) => s?.name === name);
          if (entry) entry.width = width;
        }
        grid.draw?.();
      } catch (error) {
        console.log(`lockedColumnWidths sync ${error}`);
      }
    }, [grid, lockedColumnWidths]);

    return (
      <div
        id={key}
        key={key}
        className={className}
        style={{
          ...style,
          height: '100%',
          overscrollBehavior: 'none',
          position: 'relative'
        }}
        onMouseLeave={() => setHoverCopy(null)}
        ref={element}
      >
        {hoverCopy && (
          <Tooltip label="Copy" withinPortal>
            <ActionIcon
              size={18}
              variant="default"
              onClick={() => {
                navigator.clipboard
                  .writeText(String(hoverCopy.value ?? ''))
                  .catch(err => console.log(`hover-copy writeText ${err}`));
              }}
              aria-label="Copy cell value"
              style={{
                position: 'absolute',
                top: hoverCopy.top,
                left: hoverCopy.left,
                width: 20,
                height: 20,
                minHeight: 20,
                minWidth: 20,
                padding: 0,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                zIndex: 2
              }}
            >
              <TbCopy size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
    );
  }
);

CanvasTable.displayName = 'CanvasTable';

export default CanvasTable;
